import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Button,
  Tooltip,
  Divider,
  Skeleton,
  Chip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import WebhookIcon from '@mui/icons-material/Webhook';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PhoneIcon from '@mui/icons-material/Phone';
import RefreshIcon from '@mui/icons-material/Refresh';

// Using Slack SVG icon since MUI doesn't have one
const SlackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

/**
 * Channel types
 */
export type ChannelType = 'EMAIL' | 'WEBHOOK' | 'SLACK' | 'SMS';

/**
 * Channel data structure for the card
 */
export interface ChannelCardData {
  id: string;
  name: string;
  type: ChannelType;
  enabled: boolean;
  config: {
    email?: string;
    url?: string;
    webhookUrl?: string;
    channel?: string;
    method?: string;
    phoneNumber?: string;
  };
  lastTestResult?: {
    success: boolean;
    testedAt: Date | string;
  } | null;
  verified?: boolean;
}

/**
 * ChannelCard component props
 */
interface ChannelCardProps {
  channel: ChannelCardData;
  onToggle?: (id: string, enabled: boolean) => void;
  onTest?: (id: string) => void;
  onDelete?: (id: string) => void;
  onResendVerification?: (id: string) => void;
  isToggling?: boolean;
  isTesting?: boolean;
  isResendingVerification?: boolean;
  testResult?: { success: boolean; error?: string } | null;
}

/**
 * Get icon for channel type
 */
function getChannelIcon(type: ChannelType): React.ReactNode {
  switch (type) {
    case 'EMAIL':
      return <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />;
    case 'WEBHOOK':
      return <WebhookIcon sx={{ fontSize: 32, color: 'warning.main' }} />;
    case 'SLACK':
      return (
        <Box sx={{ color: '#4A154B', fontSize: 32, display: 'flex', alignItems: 'center' }}>
          <SlackIcon />
        </Box>
      );
    case 'SMS':
      return <PhoneIcon sx={{ fontSize: 32, color: 'success.main' }} />;
    default:
      return <EmailIcon sx={{ fontSize: 32, color: 'text.secondary' }} />;
  }
}

/**
 * Get human-readable channel type label
 */
function getChannelTypeLabel(type: ChannelType): string {
  switch (type) {
    case 'EMAIL':
      return 'Email';
    case 'WEBHOOK':
      return 'Webhook';
    case 'SLACK':
      return 'Slack';
    case 'SMS':
      return 'SMS';
    default:
      return type;
  }
}

/**
 * Get config summary for display
 */
function getConfigSummary(type: ChannelType, config: ChannelCardData['config']): string {
  switch (type) {
    case 'EMAIL':
      return config.email || 'No email configured';
    case 'WEBHOOK': {
      const url = config.url || '';
      const method = config.method || 'POST';
      const domain = url ? new URL(url).hostname : 'No URL';
      return `${method} ${domain}`;
    }
    case 'SLACK': {
      if (config.channel) {
        return `#${config.channel}`;
      }
      return config.webhookUrl ? 'Webhook configured' : 'No webhook configured';
    }
    case 'SMS':
      return config.phoneNumber || 'No phone number configured';
    default:
      return 'Unknown configuration';
  }
}

/**
 * ChannelCard loading skeleton
 */
export function ChannelCardSkeleton() {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
          <Skeleton variant="rounded" width={50} height={24} />
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * ChannelCard component
 * Displays notification channel information in a card format
 */
export function ChannelCard({
  channel,
  onToggle,
  onTest,
  onDelete,
  onResendVerification,
  isToggling = false,
  isTesting = false,
  isResendingVerification = false,
  testResult,
}: ChannelCardProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Determine if channel needs verification (EMAIL type that is not verified)
  const needsVerification = channel.type === 'EMAIL' && channel.verified === false;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle?.(channel.id, event.target.checked);
  };

  const handleTest = () => {
    onTest?.(channel.id);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(channel.id);
  };

  const handleResendVerification = () => {
    onResendVerification?.(channel.id);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        },
        ...(!channel.enabled && {
          opacity: 0.7,
        }),
      }}
    >
      {/* Action Menu Button */}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          opacity: 0.6,
          '&:hover': {
            opacity: 1,
          },
        }}
        aria-label="Channel actions"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          component={RouterLink}
          to={`/notifications/${channel.id}/edit`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, p: 2.5, pr: 5 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Channel Type Icon */}
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getChannelIcon(channel.type)}
          </Box>

          {/* Channel Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {channel.name}
              </Typography>
              <Chip
                label={getChannelTypeLabel(channel.type)}
                size="small"
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
              {needsVerification && (
                <Chip
                  label="Pending Verification"
                  size="small"
                  color="warning"
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Box>

            <Tooltip title={getConfigSummary(channel.type, channel.config)}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 1,
                }}
              >
                {getConfigSummary(channel.type, channel.config)}
              </Typography>
            </Tooltip>

            {/* Last Test Result */}
            {testResult && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {testResult.success ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main">
                      Test successful
                    </Typography>
                  </>
                ) : (
                  <>
                    <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    <Tooltip title={testResult.error || 'Test failed'}>
                      <Typography variant="caption" color="error.main">
                        Test failed
                      </Typography>
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Actions Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Enable/Disable Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={channel.enabled}
              onChange={handleToggle}
              disabled={isToggling}
              size="small"
              inputProps={{
                'aria-label': `${channel.enabled ? 'Disable' : 'Enable'} ${channel.name}`,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {channel.enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Resend Verification Button (only for unverified EMAIL channels) */}
            {needsVerification && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<RefreshIcon />}
                onClick={handleResendVerification}
                disabled={isResendingVerification}
              >
                {isResendingVerification ? 'Sending...' : 'Resend'}
              </Button>
            )}

            {/* Test Button */}
            <Tooltip
              title={needsVerification ? 'Verify email address before testing' : ''}
              arrow
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleTest}
                  disabled={isTesting || !channel.enabled || needsVerification}
                >
                  {isTesting ? 'Testing...' : 'Test'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default ChannelCard;
